import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { unpackToDateTime } from '@campfire/hot-date';
import { useCampfireLazyQuery } from '../../../../../global/network/useCampfireLazyQuery';
import { VolunteerCommonProfileEmail } from '../../../volunteer-common-profile/__generated__/VolunteerCommonProfileEmail';
import { VolunteerCommonProfileSms } from '../../../volunteer-common-profile/__generated__/VolunteerCommonProfileSms';
import { VolunteerCommunicationParsed } from '../../../volunteer-common-profile/profile-sections/communications-section/VolunteerCommunicationsSection';
import { CommunicationDetailDialog } from '../../../volunteer-common-profile/profile-sections/communications-section/CommunicationDetailDialog';
import { CommunicationViewMoreDialog } from '../../../volunteer-common-profile/profile-sections/communications-section/CommunicationViewMoreDialog';
import {
  ApplicationReminderCommunication,
  toReminderCommunication,
} from '../../../volunteer-common-profile/profile-sections/communications-section/ApplicationReminderListItem';
import { applicantCommunicationTypeOptions } from '../../../volunteer-common-profile/profile-sections/communications-section/CommunicationConsts';
import { SmsLogTypeParsed } from '../../../volunteer-common-profile/VolunteerCommonProfileDialog';
import { ExpandableSidePanel } from './ExpandableSidePanel';
import { GET_APPLICANT_COMMUNICATIONS } from '../applicant-communications-model.gql';
import {
  GetApplicantCommunications,
  GetApplicantCommunicationsVariables,
} from '../__generated__/GetApplicantCommunications';

const LIMIT = 10;

export interface ApplicantReminder {
  applicationReminderId: string;
  remindedAt: any;
  tasks: Array<{ taskId: string; title: string }>;
}

type ApplicantCommunication =
  | VolunteerCommonProfileEmail
  | VolunteerCommonProfileSms
  | SmsLogTypeParsed
  | ApplicationReminderCommunication;

type ApplicantCommunicationDetail = Exclude<ApplicantCommunication, ApplicationReminderCommunication>;

const asDetailCommunication = (communication: ApplicantCommunication): ApplicantCommunicationDetail | null =>
  communication.__typename === 'VOLUNTEER_ApplicationReminderType' ? null : communication;

const communicationKey = (communication: ApplicantCommunication) => {
  if (communication.__typename === 'EMAIL_PostmarkOutboundEmailType') return communication.postmarkEmailId;
  if (communication.__typename === 'VOLUNTEER_ApplicationReminderType') return communication.applicationReminderId;
  if (communication.__typename === 'EMAIL_OutboundSmsType') return communication.outboundSmsId;
  return communication.smsLogId;
};

export const ApplicantCommunicationsSection = ({
  userId,
  applicantName,
  reminders,
  reloadSignal,
}: {
  userId?: string | null;
  applicantName: string;
  reminders: Array<ApplicantReminder>;
  reloadSignal?: number;
}) => {
  const [emails, setEmails] = useState<Array<VolunteerCommonProfileEmail>>([]);
  const [outboundSms, setOutboundSms] = useState<Array<VolunteerCommonProfileSms>>([]);
  const [viewMore, setViewMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [detailDialog, setDetailDialog] = useState<{
    open: boolean;
    communication: ApplicantCommunicationDetail | null;
  }>({ open: false, communication: null });

  const [getCommunications] = useCampfireLazyQuery<GetApplicantCommunications, GetApplicantCommunicationsVariables>(
    GET_APPLICANT_COMMUNICATIONS
  );

  const loadCommunications = useCallback(
    (isActive: () => boolean) => {
      if (!userId) {
        setEmails([]);
        setOutboundSms([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      getCommunications({ variables: { userId } })
        .then(({ data }) => {
          if (!isActive()) return;
          if (data) {
            setEmails(data.em.emails ?? []);
            setOutboundSms(data.em.outboundSms ?? []);
          }
          setLoading(false);
        })
        .catch(() => {
          if (!isActive()) return;
          setEmails([]);
          setOutboundSms([]);
          setLoading(false);
        });
    },
    [userId, getCommunications]
  );

  useEffect(() => {
    let active = true;
    loadCommunications(() => active);
    return () => {
      active = false;
    };
  }, [loadCommunications, reloadSignal]);

  const communications: Array<ApplicantCommunication> = useMemo(
    () =>
      [...emails, ...outboundSms, ...reminders.map(toReminderCommunication)].sort((a, b) => {
        if (!b.sentAt) return a.sentAt ? -1 : 1;
        if (!a.sentAt) return 1;
        return unpackToDateTime(a.sentAt) > unpackToDateTime(b.sentAt) ? -1 : 1;
      }),
    [emails, outboundSms, reminders]
  );

  return (
    <ExpandableSidePanel
      title='Communications'
      subtitle={`Communications that have been sent to ${applicantName}`}
      count={communications.length}
      onExpand={() => setViewMore(true)}
      fill
    >
      {loading && (
        <Box display='flex' justifyContent='center' padding='1.5rem 0'>
          <CircularProgress size={20} />
        </Box>
      )}

      {!loading &&
        communications.slice(0, LIMIT).map((communication) => (
          <VolunteerCommunicationParsed
            key={communicationKey(communication)}
            communication={communication}
            onDetailsClick={() => {
              const detail = asDetailCommunication(communication);
              if (detail) setDetailDialog({ open: true, communication: detail });
            }}
          />
        ))}

      {!loading && communications.length === 0 && (
        <Typography
          variant='body2'
          style={{ fontSize: '13px', color: '#494949', textAlign: 'center', padding: '1.5rem 0' }}
        >
          {userId ? 'No communications sent yet' : 'Communications are available once an account is created'}
        </Typography>
      )}

      {!loading && communications.length > LIMIT && (
        <Box display='flex' justifyContent='center'>
          <Button
            onClick={() => setViewMore(true)}
            endIcon={<ExpandMoreIcon />}
            style={{
              marginTop: '1rem',
              color: '#545454',
              padding: '2px 6px',
              fontSize: '12px',
              textTransform: 'none',
            }}
          >
            View more
          </Button>
        </Box>
      )}

      {viewMore && (
        <CommunicationViewMoreDialog
          open
          handleClose={() => setViewMore(false)}
          communications={communications}
          typeOptions={applicantCommunicationTypeOptions}
          subtitle={`Communications that have been sent to ${applicantName}`}
          onDetailsClick={(communication) => {
            const detail = asDetailCommunication(communication);
            if (detail) setDetailDialog({ open: true, communication: detail });
          }}
        />
      )}

      <CommunicationDetailDialog
        open={detailDialog.open}
        onClose={() => setDetailDialog({ open: false, communication: null })}
        communication={detailDialog.communication}
      />
    </ExpandableSidePanel>
  );
};
